import { motion } from "framer-motion";
import { Check, TrendingUp } from "lucide-react";

const bullets = [
  "Challenge inaccurate, unverifiable, and outdated items on all 3 bureaus",
  "Personal credit strategist assigned to your file",
  "Custom dispute letters, updated monthly",
  "Debt validation & credit-building playbook",
  "Track progress in a real-time client portal",
  "Pause or cancel anytime — no long contracts",
];

export default function CreditRepair({ onApply }) {
  return (
    <section
      id="credit-repair"
      data-testid="credit-repair-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-purple-600">
            Credit repair
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-black mt-3 tracking-tight text-slate-900 leading-[1.05]">
            Rebuild your score. <br />
            Unlock better funding.
          </h2>
          <p className="mt-5 text-lg text-slate-500 leading-relaxed">
            A stronger credit profile unlocks bigger approvals and lower rates.
            Our credit specialists work with all three bureaus on your behalf —
            most clients see meaningful movement in 30–60 days.
          </p>

          <ul className="mt-8 grid gap-3">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-3 text-slate-700">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-purple-100 text-purple-700 grid place-items-center flex-shrink-0">
                  <Check className="w-3 h-3" />
                </span>
                <span className="text-sm md:text-base">{b}</span>
              </li>
            ))}
          </ul>

          <button
            data-testid="credit-repair-cta"
            onClick={() => onApply?.({ funding_purpose: "Credit Repair" })}
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold px-7 py-3.5 text-sm uppercase tracking-widest shadow-xl hover:-translate-y-0.5 transition"
          >
            Start credit repair
            <TrendingUp className="w-4 h-4" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="rounded-3xl bg-gradient-to-br from-purple-100 via-fuchsia-50 to-sky-50 p-8 border border-purple-100">
            <div className="rounded-2xl bg-white p-6 shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">
                    Credit Score
                  </p>
                  <p className="font-display text-4xl font-black text-purple-700 mt-1">742</p>
                </div>
                <span className="rounded-full bg-green-100 text-green-700 text-xs font-bold px-3 py-1">
                  +137 pts
                </span>
              </div>
              <div className="space-y-3">
                {[
                  { m: "Jan", v: 605 },
                  { m: "Mar", v: 640 },
                  { m: "May", v: 688 },
                  { m: "Jul", v: 720 },
                  { m: "Sep", v: 742 },
                ].map((r) => (
                  <div key={r.m} className="flex items-center gap-3">
                    <span className="w-8 text-xs font-bold text-slate-400">{r.m}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-purple-500 to-fuchsia-500 rounded-full"
                        style={{ width: `${((r.v - 550) / 250) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-bold text-slate-700 w-10 text-right">{r.v}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs text-slate-400">
                Illustrative results. Individual results may vary.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
